import useIsMobile from '../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import appointmentService from '../services/appointmentService';
import AppointmentModal from '../components/AppointmentModal';
import EmptyState from '../components/ui/EmptyState';

const STATUS_COLORS = { programada: '#3b82f6', confirmada: '#10b981', realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b' };
const STATUS_LABELS = { programada: 'Programada', confirmada: 'Confirmada', realizada: 'Realizada', cancelada: 'Cancelada', reprogramada: 'Reprogramada' };
const TYPE_LABELS   = { firma: 'Firma', consulta: 'Consulta', entrega: 'Entrega', seguimiento: 'Seguimiento', interno: 'Interno' };
const LOC_LABELS    = { oficina: 'Oficina', domicilio_cliente: 'Domicilio del cliente', otro: 'Otro' };

const inputSt = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };

const InfoRow = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #F3F4F6', gap: 10 }}>
    <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500, textAlign: 'right' }}>{children || <span style={{ color: '#D1D5DB' }}>—</span>}</span>
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</h5>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1 }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

export default function AppointmentDetail() {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [createActivity, setCreateActivity] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleStart, setRescheduleStart] = useState('');
  const [rescheduleEnd, setRescheduleEnd] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [savingReschedule, setSavingReschedule] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await appointmentService.getById(id); setAppointment(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusSubmit = async (e) => {
    e.preventDefault(); setSavingStatus(true);
    try {
      await appointmentService.updateStatus(id, { status: newStatus, notes: statusNotes, cancellation_reason: cancelReason, create_activity: createActivity });
      setShowStatusModal(false); setStatusNotes(''); setCancelReason('');
      toast.success(newStatus === 'cancelada' ? 'Cita cancelada' : newStatus === 'realizada' ? 'Cita marcada como realizada' : 'Cita confirmada');
      load();
    } catch (err) { console.error(err); toast.error('Error al actualizar el estado de la cita'); }
    finally { setSavingStatus(false); }
  };

  const handleReschedule = async (e) => {
    e.preventDefault(); setSavingReschedule(true);
    try {
      const res = await appointmentService.reschedule(id, { start_datetime: rescheduleStart, end_datetime: rescheduleEnd, notes: rescheduleNotes });
      setShowReschedule(false);
      toast.success('Cita reprogramada correctamente');
      navigate(`/agenda/${res.data.id}`);
    } catch (err) { console.error(err); toast.error('Error al reprogramar la cita'); }
    finally { setSavingReschedule(false); }
  };

  const openStatusModal = (status) => { setNewStatus(status); setStatusNotes(''); setCancelReason(''); setCreateActivity(true); setShowStatusModal(true); };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );
  if (!appointment) return <div style={{ padding: 16, borderRadius: 10, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Cita no encontrada</div>;

  const start = new Date(appointment.start_datetime);
  const end   = new Date(appointment.end_datetime);
  const duration = Math.round((end - start) / 60000);
  const isClosed = ['realizada', 'cancelada'].includes(appointment.status);
  const statusColor = STATUS_COLORS[appointment.status];

  // Botones con jerarquía: primario (relleno) → secundario → destructivo (separado)
  const primaryBtn   = !isClosed && ['programada','confirmada'].includes(appointment.status)
    ? { label: '✓ Marcar realizada', onClick: () => openStatusModal('realizada'), primary: true }
    : null;
  const secondaryBtns = [
    { show: !isClosed, label: 'Editar',       onClick: () => setShowEdit(true),       style: { bg: '#fff',     color: '#374151', border: '#E5E7EB' } },
    { show: !isClosed, label: 'Reprogramar',  onClick: () => setShowReschedule(true), style: { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' } },
    { show: !isClosed && appointment.status === 'programada', label: 'Confirmar', onClick: () => openStatusModal('confirmada'), style: { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' } },
  ].filter(b => b.show);
  const destructiveBtn = !isClosed ? { label: 'Cancelar cita', onClick: () => openStatusModal('cancelada') } : null;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        <Link to="/agenda" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Agenda</Link>
        <span>›</span>
        <span style={{ color: '#4B5563' }}>{appointment.title}</span>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>{appointment.title}</h4>
            <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, backgroundColor: statusColor + '22', color: statusColor }}>
              {STATUS_LABELS[appointment.status]}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#9CA3AF' }}>
            {TYPE_LABELS[appointment.type]} · {LOC_LABELS[appointment.location]}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Acción primaria — rellena y destacada */}
          {primaryBtn && (
            <button onClick={primaryBtn.onClick}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', backgroundColor: '#10B981', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#10B981'}>
              {primaryBtn.label}
            </button>
          )}
          {/* Acciones secundarias */}
          {secondaryBtns.map((btn, i) => (
            <button key={i} onClick={btn.onClick}
              style={{ padding: '7px 13px', borderRadius: 8, border: `1.5px solid ${btn.style.border}`, backgroundColor: btn.style.bg, color: btn.style.color, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {btn.label}
            </button>
          ))}
          {/* Separador visual antes del destructivo */}
          {destructiveBtn && <span style={{ width: 1, height: 24, backgroundColor: '#E5E7EB', flexShrink: 0 }} />}
          {destructiveBtn && (
            <button onClick={destructiveBtn.onClick}
              style={{ padding: '7px 13px', borderRadius: 8, border: '1.5px solid #FECACA', backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {destructiveBtn.label}
            </button>
          )}
          <Link to="/agenda" style={{ padding: '7px 13px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Regresar
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: 16, alignItems: 'start' }}>

        {/* Columna principal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Info de la cita */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>Información de la cita</div>
            <div style={{ padding: '6px 18px 14px' }}>
              <InfoRow label="Fecha de inicio">
                <strong>{start.toLocaleString('es-MX', { dateStyle: 'full', timeStyle: 'short' })}</strong>
              </InfoRow>
              <InfoRow label="Hora de fin">
                {end.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
              </InfoRow>
              <InfoRow label="Duración">
                <span style={{ fontWeight: 700, color: '#4F63D2' }}>{duration} min</span>
              </InfoRow>
              <InfoRow label="Responsable">{appointment.user?.name}</InfoRow>
              <InfoRow label="Creada por">{appointment.created_by_user?.name || appointment.createdBy?.name}</InfoRow>
              {appointment.notes && <InfoRow label="Notas">{appointment.notes}</InfoRow>}
              {appointment.cancellation_reason && (
                <InfoRow label="Motivo cancelación"><span style={{ color: '#EF4444' }}>{appointment.cancellation_reason}</span></InfoRow>
              )}
            </div>
          </div>

          {/* Historial */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>Historial de cambios</div>
            {!appointment.activity_logs?.length ? (
              <EmptyState icon="📋" title="Sin cambios registrados" />
            ) : (
              <div>
                {appointment.activity_logs.map(log => (
                  <div key={log.id} style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>{log.user?.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {log.previous_status && (
                          <>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#374151' }}>{STATUS_LABELS[log.previous_status]}</span>
                            <span style={{ color: '#9CA3AF' }}>→</span>
                          </>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: STATUS_COLORS[log.new_status] + '22', color: STATUS_COLORS[log.new_status] }}>
                          {STATUS_LABELS[log.new_status]}
                        </span>
                      </div>
                      {log.notes && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{log.notes}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {new Date(log.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: isMobile ? 'static' : 'sticky', top: 20, alignSelf: 'flex-start' }}>
          {/* Cliente */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 13, color: '#111827' }}>Cliente</div>
            <div style={{ padding: '12px 14px' }}>
              {appointment.client ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>{appointment.client.name}</div>
                  {appointment.client.phone && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 2 }}>{appointment.client.phone}</div>}
                  {appointment.client.email && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>{appointment.client.email}</div>}
                  <Link to={`/clients/${appointment.client.id}`}
                    style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '5px 12px', borderRadius: 7, backgroundColor: '#EEF0FB', display: 'inline-block' }}>
                    Ver cliente
                  </Link>
                </>
              ) : <span style={{ fontSize: 13, color: '#9CA3AF' }}>Sin cliente vinculado</span>}
            </div>
          </div>

          {/* Expediente */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 13, color: '#111827' }}>Expediente</div>
            <div style={{ padding: '12px 14px' }}>
              {appointment.case_file ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                    {appointment.case_file.title || appointment.case_file.matter || `Expediente #${appointment.case_file.id}`}
                  </div>
                  {appointment.case_file.client?.name && (
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
                      {appointment.case_file.client.name}
                    </div>
                  )}
                  <Link to={`/case-files/${appointment.case_file.id}`}
                    style={{ fontSize: 12, fontWeight: 600, color: '#4F63D2', textDecoration: 'none', padding: '5px 12px', borderRadius: 7, backgroundColor: '#EEF0FB', display: 'inline-block' }}>
                    Ver expediente
                  </Link>
                </>
              ) : <span style={{ fontSize: 13, color: '#9CA3AF' }}>Sin expediente vinculado</span>}
            </div>
          </div>

          {/* Reprogramada desde */}
          {appointment.rescheduled_from && (
            <div style={{ background: '#FEF3C7', borderRadius: 10, border: '1px solid #FDE68A', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '12px 14px' }}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🔄 Reprogramada desde</p>
              {appointment.rescheduled_from.start_datetime && (
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#78350F' }}>
                  {new Date(appointment.rescheduled_from.start_datetime).toLocaleString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <Link to={`/agenda/${appointment.rescheduled_from.id}`}
                style={{ fontSize: 12, fontWeight: 600, color: '#92400E', textDecoration: 'none', padding: '4px 10px', borderRadius: 7, backgroundColor: '#FDE68A', display: 'inline-block' }}>
                Ver cita original →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Modal editar */}
      {showEdit && <AppointmentModal appointment={appointment} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}

      {/* Modal cambio de status */}
      {showStatusModal && (
        <Modal title={newStatus === 'cancelada' ? 'Cancelar cita' : newStatus === 'realizada' ? 'Marcar como realizada' : 'Confirmar cita'} onClose={() => setShowStatusModal(false)}>
          <form onSubmit={handleStatusSubmit}>
            {newStatus === 'cancelada' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Motivo de cancelación</label>
                <textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Indica el motivo..." style={{ ...inputSt, resize: 'vertical' }} />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Notas adicionales</label>
              <textarea rows={2} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="Observaciones..." style={{ ...inputSt, resize: 'vertical' }} />
            </div>
            {newStatus === 'realizada' && appointment.case_file_id && (
              <div style={{ marginBottom: 18, padding: '10px 12px', borderRadius: 8, backgroundColor: '#F0FDF4', border: '1px solid #A7F3D0' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#065F46', cursor: 'pointer', lineHeight: 1.4 }}>
                  <input type="checkbox" checked={createActivity} onChange={e => setCreateActivity(e.target.checked)} style={{ accentColor: '#10B981', marginTop: 2, flexShrink: 0 }} />
                  <span>
                    <strong>Registrar actividad en el expediente automáticamente</strong>
                    <span style={{ display: 'block', fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      Se creará una actividad de tipo "Cita" con el título, las notas y la fecha de esta cita, sin que tengas que hacerlo manualmente.
                    </span>
                  </span>
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowStatusModal(false)} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancelar</button>
              <button type="submit" disabled={savingStatus}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', backgroundColor: newStatus === 'cancelada' ? '#EF4444' : '#10B981', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: savingStatus ? 0.7 : 1 }}>
                {savingStatus ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal reprogramar */}
      {showReschedule && (
        <Modal title="Reprogramar cita" onClose={() => setShowReschedule(false)}>
          <form onSubmit={handleReschedule}>
            {[
              { label: 'Nueva fecha y hora de inicio *', name: 'start', value: rescheduleStart, onChange: e => setRescheduleStart(e.target.value) },
              { label: 'Nueva fecha y hora de fin *',    name: 'end',   value: rescheduleEnd,   onChange: e => setRescheduleEnd(e.target.value) },
            ].map(({ label, name, value, onChange }) => (
              <div key={name} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</label>
                <input type="datetime-local" required value={value} onChange={onChange} style={inputSt} />
              </div>
            ))}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Motivo del cambio</label>
              <textarea rows={2} value={rescheduleNotes} onChange={e => setRescheduleNotes(e.target.value)} placeholder="¿Por qué se reprograma?" style={{ ...inputSt, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowReschedule(false)} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancelar</button>
              <button type="submit" disabled={savingReschedule}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', backgroundColor: '#F59E0B', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: savingReschedule ? 0.7 : 1 }}>
                {savingReschedule ? 'Guardando...' : 'Reprogramar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
